import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { useToast } from '@/shared/hooks/use-toast';
import {
  createDrawingUploadBatch,
  DrawingUploadBatch,
  DrawingUploadBatchItem,
  finalizeDrawingUploadBatch,
  uploadDrawingBatchFile,
} from '../../api';
import { PDFUploadDropzone } from './PDFUploadDropzone';

type LocalRow = {
  itemId: string;
  file: File;
  uploadProgress: number;
  uploadStatus: 'pending' | 'uploading' | 'uploaded' | 'failed';
  error?: string;
};

interface PDFBatchUploadManagerProps {
  projectId: number;
  initialFiles?: File[];
  onReset?: () => void;
  onUploadComplete?: (batch: DrawingUploadBatch) => void | Promise<void>;
}

const UPLOAD_CONCURRENCY = 4;

export const PDFBatchUploadManager: React.FC<PDFBatchUploadManagerProps> = ({
  projectId,
  initialFiles,
  onReset,
  onUploadComplete,
}) => {
  const { toast } = useToast();
  const [rows, setRows] = useState<LocalRow[]>([]);
  const [isStarting, setIsStarting] = useState(() => Boolean(initialFiles?.length));
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isRefreshingList, setIsRefreshingList] = useState(false);
  const [completedBatch, setCompletedBatch] = useState<DrawingUploadBatch | null>(null);
  const startedInitialFiles = useRef<File[] | null>(null);
  const uploadSummary = useMemo(() => {
    if (!rows.length) return { uploaded: 0, failed: 0, total: 0, averageProgress: 0 };
    const uploaded = rows.filter((row) => row.uploadStatus === 'uploaded').length;
    const failed = rows.filter((row) => row.uploadStatus === 'failed').length;
    const uploadProgress = Math.round(rows.reduce((sum, row) => sum + row.uploadProgress, 0) / rows.length);
    const visibleProgress = Math.min(90, Math.round(uploadProgress * 0.9));
    return { uploaded, failed, total: rows.length, uploadProgress, visibleProgress };
  }, [rows]);

  const uploadRows = useCallback(async (
    batchId: string,
    items: DrawingUploadBatchItem[],
    localRows: LocalRow[]
  ) => {
    const queue = [...items];
    const failedUploads = new Set<string>();
    async function worker() {
      while (queue.length) {
        const item = queue.shift();
        if (!item?.signed_upload_url) continue;
        const local = localRows.find((row) => row.itemId === item.id);
        if (!local) continue;
        setRows((current) => current.map((row) => (
          row.itemId === item.id ? { ...row, uploadStatus: 'uploading', error: undefined } : row
        )));
        try {
          await uploadDrawingBatchFile(item.signed_upload_url, local.file, (progress) => {
            setRows((current) => current.map((row) => (
              row.itemId === item.id ? { ...row, uploadProgress: progress } : row
            )));
          });
          setRows((current) => current.map((row) => (
            row.itemId === item.id ? { ...row, uploadStatus: 'uploaded', uploadProgress: 100 } : row
          )));
        } catch (error) {
          failedUploads.add(item.id);
          setRows((current) => current.map((row) => (
            row.itemId === item.id
              ? { ...row, uploadStatus: 'failed', error: error instanceof Error ? error.message : 'Upload failed' }
              : row
          )));
        }
      }
    }

    await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, queue.length) }, worker));
    setIsFinalizing(true);
    try {
      const finalized = await finalizeDrawingUploadBatch(
        batchId,
        items.map((item) => item.id),
        Array.from(failedUploads)
      );
      setIsRefreshingList(true);
      await onUploadComplete?.(finalized.batch);
      setCompletedBatch(finalized.batch);
      setRows([]);
      if (failedUploads.size) {
        toast({
          title: 'Some uploads failed',
          description: 'The browser could not upload some files to storage. Check bucket CORS settings or retry.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Uploads complete',
          description: 'Your drawings are now available. Background processing will finish shortly.',
          variant: 'default',
          className: 'bg-green-500 text-white',
        });
      }
    } finally {
      setIsFinalizing(false);
      setIsRefreshingList(false);
    }
  }, [onUploadComplete, toast]);

  const handleFilesSelect = useCallback(async (files: File[], rejected: File[]) => {
    if (rejected.length) {
      toast({
        title: 'Some files were skipped',
        description: `${rejected.length} non-PDF file(s) were ignored.`,
        variant: 'default',
      });
    }
    if (!files.length) return;

    setIsStarting(true);
    setRows([]);
    setCompletedBatch(null);
    try {
      const init = await createDrawingUploadBatch(projectId, files.map((file) => ({
        file_name: file.name,
        content_type: file.type || 'application/pdf',
        file_size: file.size,
      })));
      const localRows = init.items.map((item, index) => ({
        itemId: item.id,
        file: files[index],
        uploadProgress: 0,
        uploadStatus: 'pending' as const,
      }));
      setRows(localRows);
      await uploadRows(init.id, init.items, localRows);
    } catch (error) {
      toast({
        title: 'Batch upload failed',
        description: error instanceof Error ? error.message : 'Could not start batch upload.',
        variant: 'destructive',
      });
    } finally {
      setIsStarting(false);
    }
  }, [projectId, toast, uploadRows]);

  useEffect(() => {
    if (!initialFiles?.length || startedInitialFiles.current === initialFiles) return;
    startedInitialFiles.current = initialFiles;
    void handleFilesSelect(initialFiles, []);
  }, [handleFilesSelect, initialFiles]);

  return (
    <div className="space-y-4">
      {!completedBatch && !rows.length && !isStarting && (
        <PDFUploadDropzone
          multiple
          onFileSelect={() => undefined}
          onFilesSelect={handleFilesSelect}
          isLoading={isStarting}
          title="Batch Upload Drawing PDFs"
          description="Drop a drawing set to upload PDFs directly to storage, then validate extracted sheet data."
          buttonLabel="Choose PDFs"
          minHeightClassName="min-h-[260px]"
        />
      )}

      {!completedBatch && !rows.length && isStarting && (
        <Card>
          <CardContent className="flex min-h-[180px] items-center justify-center py-8">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">Preparing batch upload...</p>
                <p className="text-xs text-muted-foreground">
                  Securing storage for your drawing files.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {!completedBatch && rows.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Uploading Drawing Set</CardTitle>
            <p className="text-xs text-muted-foreground">
              Uploading PDFs directly to storage. Backend processing starts automatically after upload and should only take a few minutes.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${isRefreshingList ? 100 : uploadSummary.visibleProgress}%` }}
              />
            </div>
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span>
                {isRefreshingList
                  ? '100% ready'
                  : `${uploadSummary.visibleProgress}% complete`}
              </span>
              <span>{uploadSummary.uploaded}/{uploadSummary.total} files uploaded</span>
              {uploadSummary.failed > 0 ? <span>{uploadSummary.failed} failed</span> : null}
              {isFinalizing ? <span>Starting background processing...</span> : null}
              {isRefreshingList ? <span>Refreshing drawing list...</span> : null}
            </div>
          </CardContent>
        </Card>
      )}

      {completedBatch && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Uploads Complete</CardTitle>
            <p className="text-xs text-muted-foreground">
              {completedBatch.total_files - completedBatch.failed_files}/{completedBatch.total_files} drawing file(s) uploaded.
              Background processing is running and should finish shortly.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setCompletedBatch(null);
                  setRows([]);
                  startedInitialFiles.current = null;
                  onReset?.();
                }}
              >
                Start New Upload
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PDFBatchUploadManager;
